<template>
  <div class="salon-app salon-promo-page" :class="{ 'salon-promo-page--browse': browse }">
    <!-- Top app bar. No progress rail: the voucher picker is a detour off SH009, not a
         tenth step of the booking — and in browse mode (SH016's "Awards") it is not part
         of a booking at all. -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ browse ? $t("salon.promo.titleBrowse") : $t("salon.promo.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
    </header>

    <main class="salon-main">
      <form class="salon-search" @submit.prevent="getList">
        <span class="salon-search-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="1.8" />
            <path d="M16 16l4 4" fill="none" stroke="currentColor" stroke-width="1.8"
              stroke-linecap="round" />
          </svg>
        </span>
        <input
          v-model="keyword"
          class="salon-search-input"
          type="text"
          :placeholder="$t('salon.promo.searchPlaceholder')"
          :aria-label="$t('salon.promo.searchLabel')"
        />
        <button class="salon-search-btn" type="submit">{{ $t("salon.promo.search") }}</button>
      </form>

      <div class="salon-voucher-list">
        <div
          v-for="entry in stackedVouchers"
          :key="entry.key"
          class="salon-voucher"
          :class="{
            'salon-voucher--static': browse,
            'salon-voucher--selected': !browse && isSelected(entry),
            'salon-voucher--spent': !browse && !isEntryUsable(entry) && !isSelected(entry),
          }"
          :role="browse ? null : 'button'"
          :tabindex="browse ? null : 0"
          :aria-pressed="browse ? null : String(isSelected(entry))"
          @click="select(entry)"
          @keydown.enter.prevent="select(entry)"
          @keydown.space.prevent="select(entry)"
        >
          <img
            v-if="entry.item.icon_url && !brokenImages.includes(entry.key)"
            class="salon-voucher-icon"
            :src="entry.item.icon_url"
            :alt="entry.item.reward_title"
            @error="onImageError(entry)"
          />
          <span v-else class="salon-voucher-icon salon-voucher-icon--blank" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 000-4V8z"
                fill="none" stroke="currentColor" stroke-width="1.8"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>

          <div class="salon-voucher-body">
            <h3 class="salon-voucher-title">{{ entry.item.reward_title }}</h3>
            <p v-if="entry.item.expiry_date_text" class="salon-voucher-expiry">
              {{ $t("salon.promo.validUntil", { date: entry.item.expiry_date_text }) }}
            </p>
            <!-- A stack of N can have one of its copies already on this booking, so say
                 how many are still free rather than letting the whole stack read as used. -->
            <p v-if="!browse && entry.count > 1" class="salon-voucher-stock">
              {{ $t("salon.promo.available", { count: entry.usableCount }) }}<template v-if="entry.usedCount">
                · {{ $t("salon.promo.inUse", { count: entry.usedCount }) }}</template>
            </p>
          </div>

          <div class="salon-voucher-tail">
            <span v-if="entry.count > 1" class="salon-voucher-count">x{{ entry.count }}</span>
            <span v-if="!browse && isSelected(entry)" class="salon-voucher-check" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <circle cx="12" cy="12" r="10" fill="currentColor" />
                <path d="M8 12.5l2.5 2.5L16 9.5" fill="none" stroke="#ffffff" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </div>
        </div>

        <p v-if="!isLoading && loadFailed" class="salon-empty salon-empty--error">
          {{ $t("salon.promo.loadError") }}
          <button class="salon-retry" type="button" @click="getList">{{ $t("salon.bookings.retry") }}</button>
        </p>
        <p v-else-if="!isLoading && !stackedVouchers.length" class="salon-empty">
          {{ emptyText }}
        </p>
      </div>
    </main>

    <nav v-if="!browse" class="salon-cta">
      <p v-if="errorMessage" class="salon-error" role="alert">{{ errorMessage }}</p>
      <!-- Removing the voucher has to be as reachable as applying one; a customer who
           picked the wrong voucher on SH009 has nowhere else to undo it. -->
      <button
        v-if="appliedVoucherId"
        class="salon-cta-secondary"
        type="button"
        @click="removeVoucher"
      >
        {{ $t("salon.review.removeVoucher") }}
      </button>
      <button class="salon-cta-btn" type="button" :disabled="!selected || isApplying" @click="apply">
        {{ isApplying ? $t("salon.promo.applying") : $t("salon.promo.apply") }}
      </button>
    </nav>

    <material-loading :active="isLoading || isApplying" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import PromocodeService from "@/services/promocode.service";
import "@/salon/styles/tokens.css";

/**
 * SH009 — the voucher picker behind "Apply promo code..." on the review screen.
 *
 * Cloned from the MOO cart's own picker (src/views/SelectPromoCode.vue): same two calls,
 * same rules, restyled into the salon system. It stays a step of BookingFlow rather than a
 * route of its own, because the booking being priced lives in that container's state and a
 * route change would lose it.
 */
export default {
  name: "SalonSelectPromoCode",
  components: {
    MaterialLoading,
    SalonProfileMenu,
  },
  props: {
    /**
     * SH016 — "Awards": the same list, with nothing to apply a voucher to.
     *
     * Everything that only means something against a booking goes away — the Apply and
     * Remove buttons, the picked state, and the per-stack "in use" tally — so what is
     * left is simply what the member owns.
     */
    browse: {
      type: Boolean,
      default: false,
    },
    // SH016 — the avatar in the top bar.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    memberId: {
      type: [Number, String],
      default: "",
    },
    branchCode: {
      type: String,
      default: "",
    },
    // The basket being priced, in the shape the ordering API's voucher rules read: an
    // item-restricted voucher is matched on id and group, and the discount base is
    // member_price × quantity.
    services: {
      type: Array,
      default: () => [],
    },
    subtotal: {
      type: Number,
      default: 0,
    },
    /**
     * The voucher already on the booking.
     *
     * A member can own several copies of the same reward, and `get-promo-code` returns one
     * row per copy sharing a single reward `id` — so identity here is `voucher_id`, the
     * member-voucher instance, exactly as MOO's reward list pins its Applied state.
     */
    appliedVoucherId: {
      type: [Number, String],
      default: "",
    },
  },
  emits: ["back", "applied", "removed", "profile"],
  data() {
    return {
      isLoading: false,
      isApplying: false,
      loadFailed: false,
      errorMessage: "",
      keyword: "",
      vouchers: [],
      // The chosen member-voucher instance, not the stack it came from.
      selected: null,
      brokenImages: [],
    };
  },
  computed: {
    /**
     * Collapse duplicate copies of the same reward into one card carrying an "xN" count —
     * the same shape MOO's reward list builds in `stackedRewards` (MyReward.vue).
     *
     * `get-promo-code` returns a row per member-voucher instance and selects
     * `minty_mer_rewards.*`, so all four copies of "Bill Dis $10" arrive with an identical
     * `id`. Rendering them as four cards keyed on that id made picking one light up all
     * four; grouping on it is what the id is actually good for.
     *
     * The representative `item` is deliberately a copy that can still be used, so applying
     * from a stack whose first copy is already on this booking still hands over a free one.
     */
    stackedVouchers() {
      const list = Array.isArray(this.vouchers) ? this.vouchers : [];
      const entries = [];
      const indexByKey = new Map();

      list.forEach((voucher) => {
        const key = this.keyOf(voucher);
        const usable = !this.isInUse(voucher);

        if (!indexByKey.has(key)) {
          indexByKey.set(key, entries.length);
          entries.push({
            key,
            item: voucher,
            count: 1,
            usableCount: usable ? 1 : 0,
            usedCount: usable ? 0 : 1,
          });
          return;
        }

        const entry = entries[indexByKey.get(key)];
        entry.count += 1;
        entry.usableCount += usable ? 1 : 0;
        entry.usedCount += usable ? 0 : 1;

        // Keep a usable copy as the one this card would hand over.
        if (this.isInUse(entry.item) && usable) {
          entry.item = voucher;
        }
      });

      return entries;
    },

    emptyText() {
      if (this.keyword) {
        return this.$t("salon.promo.noMatch");
      }

      return this.browse
        ? this.$t("salon.promo.emptyBrowse")
        : this.$t("salon.promo.empty");
    },
  },
  created() {
    this.getList();
  },
  methods: {
    // Copies of one reward share an id, so that is the grouping key; a row with no id at
    // all falls back to its title rather than becoming its own singleton stack.
    keyOf(voucher) {
      return voucher && voucher.id != null
        ? "id:" + voucher.id
        : "name:" + ((voucher && voucher.reward_title) || "");
    },

    // "In use" means this exact copy is the one already on the booking. `get-promo-code`
    // filters locked vouchers out server-side, so that is the only way a copy is spoken for.
    isInUse(voucher) {
      return (
        !!this.appliedVoucherId &&
        !!voucher &&
        String(voucher.voucher_id) === String(this.appliedVoucherId)
      );
    },

    /**
     * Matched on the stack, not on the copy.
     *
     * One card is one reward and only one voucher can be on a booking, so "is this the
     * chosen card" is a question about the stack. Comparing copies would leave the card
     * unmarked whenever the chosen copy is the one already in use — the stack's
     * representative is deliberately a free copy, so the two never line up.
     */
    isSelected(entry) {
      return !!this.selected && !!entry && this.keyOf(this.selected) === entry.key;
    },

    // A stack is pickable while it still holds at least one free copy.
    isEntryUsable(entry) {
      return !!entry && entry.usableCount > 0;
    },

    select(entry) {
      if (this.browse) {
        return;
      }

      if (!this.isEntryUsable(entry) && !this.isSelected(entry)) {
        return;
      }

      this.selected = entry.item;
      this.errorMessage = "";
    },

    onImageError(entry) {
      if (!this.brokenImages.includes(entry.key)) {
        this.brokenImages.push(entry.key);
      }
    },

    async getList() {
      if (!this.memberId) {
        return;
      }

      this.isLoading = true;
      this.loadFailed = false;

      try {
        const response = await PromocodeService.getListPromocode({
          memberId: this.memberId,
          search_keyword: this.keyword,
        });

        if (response.data.result.isSuccess) {
          // This endpoint returns the list as `data` itself, not wrapped in a key.
          this.vouchers = response.data.data ?? [];
          this.restoreSelection();
        } else {
          this.vouchers = [];
          this.loadFailed = true;
        }
      } catch (error) {
        this.vouchers = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    restoreSelection() {
      if (!this.appliedVoucherId) {
        return;
      }

      this.selected =
        this.vouchers.find((voucher) => this.isInUse(voucher)) || null;
    },

    /**
     * The basket as the voucher rules expect it. One appointment books one of each
     * service, so quantity is always 1; add-ons ride inside the service's price rather
     * than as lines of their own, the way they do everywhere else in this flow.
     */
    voucherItems() {
      return this.services.map((service) => {
        const addOns = (service.add_ons || []).reduce(
          (sum, addOn) => sum + Number(addOn.price || 0),
          0
        );

        return {
          id: service.id,
          // Salon services carry no report group, and the rules only ever test this
          // against a list of group ids — 0 matches nothing, which is the honest answer.
          group_id: 0,
          member_price: Number(service.price || 0) + addOns,
          quantity: 1,
        };
      });
    },

    async apply() {
      if (!this.selected || this.isApplying) {
        return;
      }

      this.isApplying = true;
      this.errorMessage = "";

      try {
        const response = await PromocodeService.calculateVoucherPrice({
          branch_code: this.branchCode,
          memberId: this.memberId,
          voucherCode: this.selected.code,
          totalOrder: this.subtotal,
          items: this.voucherItems(),
        });

        if (!response.data.result.isSuccess) {
          this.errorMessage =
            response.data.result.message || this.$t("salon.promo.cannotUse");
          return;
        }

        const data = response.data.data || {};

        // A voucher can be valid and still apply to nothing in this basket; the API says
        // so in error_message rather than by failing the call.
        if (data.error_message) {
          this.errorMessage = data.error_message;
          return;
        }

        this.$emit("applied", {
          promocode_id: this.selected.id,
          // Which copy was spent. The reward id cannot say that — the member owns several.
          promocode_voucher_id: this.selected.voucher_id,
          promocode_name: this.selected.reward_title || "",
          promocode_code: this.selected.code || "",
          discount_amount: Number(data.voucher_deducted || 0),
        });
      } catch (error) {
        this.errorMessage =
          this.$t("salon.promo.networkError");
      } finally {
        this.isApplying = false;
      }
    },

    removeVoucher() {
      this.selected = null;
      this.$emit("removed");
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-promo-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 12px;
  background: var(--salon-surface);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--salon-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-primary);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--salon-stack-md);
  padding: var(--salon-stack-md) var(--salon-container-padding) 140px;
}

/* Browse mode has no fixed apply bar, so the list does not have to clear one. */
.salon-promo-page--browse .salon-main {
  padding-bottom: 40px;
}

.salon-search {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px 6px 6px 14px;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-variant);
  border-radius: 999px;
  box-shadow: var(--salon-shadow-soft);
}

.salon-search-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: var(--salon-on-surface-variant);
}

.salon-search-input {
  flex: 1;
  min-width: 0;
  padding: 8px 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
  background: transparent;
  border: 0;
  outline: none;
}

.salon-search-input::placeholder {
  color: var(--salon-on-surface-variant);
}

.salon-search-btn {
  flex-shrink: 0;
  padding: 9px 18px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.salon-search-btn:active {
  transform: scale(0.97);
}

/* ── Voucher cards ─────────────────────────────────────────────── */
.salon-voucher-list {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
}

.salon-voucher {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  background: var(--salon-surface-lowest);
  border: 1.5px solid transparent;
  border-radius: 12px;
  box-shadow: var(--salon-shadow-soft);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.salon-voucher:active {
  transform: scale(0.99);
}

.salon-voucher--static,
.salon-voucher--static:active {
  cursor: default;
  transform: none;
}

/* Gold, the same mark SH004 and SH008 use for a chosen card. */
.salon-voucher--selected {
  border-color: var(--salon-gold);
  box-shadow: 0 0 15px rgba(201, 162, 39, 0.3);
}

.salon-voucher-icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 10px;
}

.salon-voucher-icon--blank {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-variant);
}

.salon-voucher-body {
  flex: 1;
  min-width: 0;
}

/* material-dashboard forces Roboto Slab onto every h1-h3 globally, and that beats a scoped
   rule that simply omits font-family. Every heading in this file therefore names its own
   face — see the MTH027 PaymentSuccess reskin for the same trap. */
.salon-voucher-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-voucher-expiry {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-voucher-stock {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-secondary);
}

.salon-voucher-tail {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
  align-items: flex-end;
  align-self: stretch;
  justify-content: space-between;
}

/* "x4" — how many copies of this reward the member holds. */
.salon-voucher-count {
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #241a00;
  background: rgba(254, 210, 85, 0.25);
  border-radius: 6px;
}

.salon-voucher-check {
  flex-shrink: 0;
  color: var(--salon-gold);
}

/* Every copy of this reward is already on the booking. */
.salon-voucher--spent {
  cursor: not-allowed;
  opacity: 0.5;
}

.salon-voucher--spent:active {
  transform: none;
}

.salon-empty {
  padding: 32px 0;
  margin: 0;
  font-size: 14px;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

.salon-empty--error {
  color: var(--salon-error);
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 12px auto 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}

/* ── Bottom action bar ─────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(20px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface-lowest);
  transform: translateX(-50%);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-error {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--salon-error);
  text-align: center;
}

.salon-cta-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary-container);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.salon-cta-secondary {
  width: 100%;
  padding: 12px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-cta-secondary:hover {
  color: var(--salon-primary);
}
</style>
